/**
 * As peças repetidas de uma tela de listagem: KPI clicável, chips de recorte,
 * paginação, menu de linha e estados vazios.
 *
 * Existem como componente, e não coladas dentro da primeira tela que precisou
 * delas, porque toda listagem do sistema faz as mesmas cinco coisas — e
 * escrever a sexta à mão é como o foco some de um controle e a contagem some
 * de um chip. Os tamanhos, raios e cores vêm do catálogo de componentes
 * (`design/prototipos/Lumma Fit Componentes.dc.html`) e da spec 01: KPI em
 * cartão branco com ícone em tint de 32px, chip em pill de 999px, paginação em
 * botões de 34px, menu de ações em popover de 8px de raio.
 *
 * Nenhuma delas conhece nota fiscal, produto ou cliente: recebem texto e
 * número prontos.
 */
import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Icone, type NomeIcone } from './Icone'

/* ------------------------------------------------------------------- KPI */

/**
 * O tom decide a cor do ícone e do dado de apoio — nunca a do número.
 *
 * `atencao` e `erro` são reservados a estado que pede alguém agir hoje (spec
 * 01, "Status"). KPI que fica vermelho porque o número é grande ensina a
 * ignorar vermelho.
 */
export type TomKPI = 'neutro' | 'atencao' | 'erro' | 'ok'

const TINTA_DO_TOM: Record<TomKPI, { caixa: string; icone: string; apoio: string }> = {
  neutro: {
    caixa: 'bg-divisoria',
    icone: 'text-texto',
    apoio: 'text-texto-suave',
  },
  ok: { caixa: 'bg-ok-suave', icone: 'text-ok-texto', apoio: 'text-ok-texto' },
  atencao: {
    caixa: 'bg-atencao-suave',
    icone: 'text-atencao-texto',
    apoio: 'text-atencao-texto',
  },
  erro: { caixa: 'bg-erro-suave', icone: 'text-erro-texto', apoio: 'text-erro-texto' },
}

/**
 * Cartão de indicador. Quando recebe `aoClicar`, ele RECORTA a tabela — e por
 * isso é um `button` com `aria-pressed`, não uma `div` bonita: quem navega por
 * teclado precisa alcançar o filtro, e quem usa leitor de tela precisa ouvir
 * que ele está ligado.
 */
export function CartaoKPI({
  icone,
  rotulo,
  valor,
  apoio,
  tom = 'neutro',
  ativo = false,
  aoClicar,
}: {
  icone: NomeIcone
  rotulo: string
  valor: string
  apoio: string
  tom?: TomKPI
  ativo?: boolean
  aoClicar?: () => void
}) {
  const cores = TINTA_DO_TOM[tom]

  const conteudo = (
    <>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[12.5px] text-texto-suave">{rotulo}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-sistema ${cores.caixa}`}>
          <Icone nome={icone} tamanho={18} className={cores.icone} />
        </span>
      </div>
      <div className="tabular text-[21px] leading-none font-bold tracking-[-0.3px] text-tinta">
        {valor}
      </div>
      <div className={`mt-2 text-xs ${tom === 'neutro' ? 'text-texto-suave' : cores.apoio}`}>
        {apoio}
      </div>
    </>
  )

  const base = `rounded-sistema border bg-superficie px-5 py-4 text-left shadow-[var(--shadow-cartao)] ${
    ativo ? 'border-marca ring-2 ring-marca/15' : 'border-borda'
  }`

  if (!aoClicar) {
    return <div className={base}>{conteudo}</div>
  }

  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      className={`${base} hover:border-marca-borda`}
    >
      {conteudo}
    </button>
  )
}

/* ----------------------------------------------------------------- chips */

export type Chip = {
  chave: string
  rotulo: string
  contagem?: number
  /**
   * Chip que aparece mesmo com contagem zero. É o "Todas": sumir com ele
   * deixaria a pessoa sem caminho de volta depois de filtrar até o vazio.
   */
  sempre?: boolean
}

/**
 * Chips de recorte. Chip de contagem zero some — a menos que seja o escolhido
 * ou marcado `sempre`: filtro que não filtra nada só ocupa a linha.
 */
export function Chips({
  chips,
  valor,
  aoTrocar,
  rotuloDoGrupo,
}: {
  chips: Chip[]
  valor: string
  aoTrocar: (chave: string) => void
  rotuloDoGrupo: string
}) {
  const visiveis = chips.filter((c) => c.sempre || c.chave === valor || (c.contagem ?? 0) > 0)

  return (
    <div role="group" aria-label={rotuloDoGrupo} className="flex flex-wrap items-center gap-2">
      {visiveis.map((chip) => {
        const ativo = chip.chave === valor
        return (
          <button
            key={chip.chave || 'todas'}
            type="button"
            aria-pressed={ativo}
            onClick={() => aoTrocar(chip.chave)}
            className={`inline-flex h-[34px] items-center gap-2 rounded-full border px-3.5 text-[13px] font-semibold ${
              ativo
                ? 'border-marca bg-marca-suave text-marca-forte'
                : 'border-borda bg-superficie text-texto hover:border-inativo'
            }`}
          >
            {chip.rotulo}
            {chip.contagem !== undefined && (
              <span
                className={`tabular rounded-full px-1.5 py-px text-[11px] font-bold ${
                  ativo ? 'bg-superficie text-marca-forte' : 'bg-divisoria text-texto-suave'
                }`}
              >
                {chip.contagem}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------- paginação */

/**
 * Paginação com a contagem à esquerda e os botões à direita.
 *
 * A contagem diz "1–20 de 348", e não só o número da página: o que a pessoa
 * quer saber é quanto falta, não em que página está.
 */
export function Paginacao({
  pagina,
  paginas,
  total,
  tamanho,
  substantivo,
  aoTrocarPagina,
}: {
  pagina: number
  paginas: number
  total: number
  tamanho: number
  /** No singular: "nota", "produto". O plural sai daqui com "s". */
  substantivo: string
  aoTrocarPagina: (pagina: number) => void
}) {
  if (total === 0) return null

  const primeiro = (pagina - 1) * tamanho + 1
  const ultimo = Math.min(pagina * tamanho, total)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-borda px-4 py-2.5">
      <span className="text-[13px] text-texto-suave">
        <span className="tabular">
          {primeiro}–{ultimo}
        </span>{' '}
        de <span className="tabular">{total}</span> {total === 1 ? substantivo : `${substantivo}s`}
      </span>

      {paginas > 1 && (
        <div className="flex items-center gap-1.5">
          <BotaoDePagina
            rotulo="Página anterior"
            desabilitado={pagina <= 1}
            aoClicar={() => aoTrocarPagina(pagina - 1)}
          >
            <Icone nome="chevron_esquerda" tamanho={17} />
          </BotaoDePagina>

          {paginasVisiveis(pagina, paginas).map((numero, i) =>
            numero === null ? (
              <span key={`corte-${i}`} className="px-1 text-[13px] text-inativo">
                …
              </span>
            ) : (
              <BotaoDePagina
                key={numero}
                rotulo={`Página ${numero}`}
                atual={numero === pagina}
                aoClicar={() => aoTrocarPagina(numero)}
              >
                <span className="tabular">{numero}</span>
              </BotaoDePagina>
            ),
          )}

          <BotaoDePagina
            rotulo="Próxima página"
            desabilitado={pagina >= paginas}
            aoClicar={() => aoTrocarPagina(pagina + 1)}
          >
            <Icone nome="chevron_direita" tamanho={17} />
          </BotaoDePagina>
        </div>
      )}
    </div>
  )
}

function BotaoDePagina({
  rotulo,
  children,
  atual = false,
  desabilitado = false,
  aoClicar,
}: {
  rotulo: string
  children: ReactNode
  atual?: boolean
  desabilitado?: boolean
  aoClicar: () => void
}) {
  return (
    <button
      type="button"
      disabled={desabilitado}
      aria-label={rotulo}
      aria-current={atual ? 'page' : undefined}
      onClick={aoClicar}
      className={`flex h-[34px] min-w-[34px] items-center justify-center rounded-sistema border px-2 text-[13px] font-semibold ${
        atual
          ? 'border-marca bg-marca-suave text-marca-forte'
          : 'border-borda bg-superficie text-texto hover:border-inativo disabled:text-inativo disabled:hover:border-borda'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * As páginas que cabem sem virar uma régua: as vizinhas, as pontas e o corte.
 */
function paginasVisiveis(pagina: number, paginas: number): (number | null)[] {
  if (paginas <= 7) return Array.from({ length: paginas }, (_, i) => i + 1)

  const perto = new Set<number>([1, paginas, pagina, pagina - 1, pagina + 1])
  const lista: (number | null)[] = []
  let anterior = 0

  for (let numero = 1; numero <= paginas; numero++) {
    if (!perto.has(numero)) continue
    if (anterior && numero - anterior > 1) lista.push(null)
    lista.push(numero)
    anterior = numero
  }
  return lista
}

/* -------------------------------------------------------- menu de linha */

export type AcaoDeLinha = {
  rotulo: string
  icone: NomeIcone
  aoEscolher: () => void
  /** Quando presente, o item fica desabilitado e a razão aparece embaixo. */
  razaoImpedida?: string
  destrutiva?: boolean
  /** Separador acima deste item. */
  separar?: boolean
}

/**
 * O menu ⋮ de uma linha de tabela.
 *
 * Ação impedida fica DESABILITADA com a razão escrita embaixo, nunca oferecida
 * para falhar depois do clique — e a razão é texto, não `title`: o navegador
 * não mostra `title` de controle desabilitado.
 */
export function MenuDeLinha({ rotulo, acoes }: { rotulo: string; acoes: AcaoDeLinha[] }) {
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })
  const botao = useRef<HTMLButtonElement>(null)

  /*
   * O menu é desenhado num PORTAL, com posição fixa medida a partir do botão.
   *
   * Posicionado em `absolute` dentro da linha, ele era cortado: a tabela vive
   * num cartão com `overflow:hidden` e num invólucro com `overflow-x:auto`, e
   * qualquer ancestral com overflow recorta o filho posicionado. Na última
   * linha aparecia só uma tira branca — o menu estava lá, escondido atrás da
   * borda do cartão. Portal para o `body` não tem ancestral que recorte.
   */
  useLayoutEffect(() => {
    if (!aberto) return

    const medir = () => {
      const alvo = botao.current?.getBoundingClientRect()
      if (!alvo) return
      const largura = 240
      const altura = Math.min(acoes.length * 46 + 12, 320)

      // Abre para cima quando não cabe embaixo, e nunca sangra pela direita.
      const cabeEmbaixo = alvo.bottom + altura + 8 <= window.innerHeight
      setPosicao({
        top: cabeEmbaixo ? alvo.bottom + 4 : Math.max(8, alvo.top - altura - 4),
        left: Math.max(8, Math.min(alvo.right - largura, window.innerWidth - largura - 8)),
      })
    }

    medir()
    // `capture` para pegar a rolagem de QUALQUER ancestral — a da página e a
    // horizontal da própria tabela.
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    return () => {
      window.removeEventListener('scroll', medir, true)
      window.removeEventListener('resize', medir)
    }
  }, [aberto, acoes.length])

  useEffect(() => {
    if (!aberto) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aberto])

  return (
    <div className="flex justify-end">
      <button
        ref={botao}
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((estava) => !estava)}
        className={`flex h-8 w-8 items-center justify-center rounded-sistema ${
          aberto ? 'bg-divisoria text-tinta' : 'text-texto-suave hover:bg-divisoria'
        }`}
      >
        <Icone nome="more_vert" tamanho={19} />
        <span className="sr-only">{rotulo}</span>
      </button>

      {aberto &&
        createPortal(
          <>
            {/* Camada de clique-fora: menu que só fecha no próprio botão fica
                aberto para sempre quando a pessoa clica em outro lugar. */}
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setAberto(false)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              role="menu"
              style={{ top: posicao.top, left: posicao.left, width: 240 }}
              className="fixed z-50 rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
            >
              {acoes.map((acao) => (
                <div key={acao.rotulo}>
                  {acao.separar && <div className="my-1 border-t border-divisoria" />}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={!!acao.razaoImpedida}
                    onClick={() => {
                      setAberto(false)
                      acao.aoEscolher()
                    }}
                    className={`flex w-full flex-col items-start gap-0.5 px-3.5 py-2 text-left text-[13.5px] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
                      acao.destrutiva
                        ? 'text-erro-texto hover:bg-erro-suave disabled:hover:bg-transparent'
                        : 'text-texto hover:bg-divisoria disabled:hover:bg-transparent'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Icone nome={acao.icone} tamanho={17} />
                      {acao.rotulo}
                    </span>
                    {acao.razaoImpedida && (
                      <span className="pl-[27px] text-[11.5px] text-texto-suave">
                        {acao.razaoImpedida}
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </>,
          document.body,
        )}
    </div>
  )
}

/* ------------------------------------------------------- estados vazios */

/**
 * Vazio de primeira vez: a lista nunca teve nada. Explica o que a função faz e
 * oferece o caminho — não é erro, é começo.
 */
export function VazioPrimeiraVez({
  icone,
  titulo,
  descricao,
  acao,
}: {
  icone: NomeIcone
  titulo: string
  descricao: string
  acao?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <Icone nome={icone} tamanho={26} className="text-inativo" />
      <p className="text-sm font-semibold text-tinta">{titulo}</p>
      <p className="max-w-[420px] text-[13px] leading-[1.55] text-texto-suave">{descricao}</p>
      {acao && (
        <div className="mt-1.5 flex flex-wrap items-center justify-center gap-2.5">{acao}</div>
      )}
    </div>
  )
}

/**
 * Vazio de filtro: existe conteúdo, o recorte é que não achou. Diz quantos
 * existem fora dele e oferece limpar — sem isso a pessoa conclui que perdeu os
 * dados.
 */
export function VazioFiltrado({
  titulo,
  descricao,
  aoLimpar,
}: {
  titulo: string
  descricao: string
  aoLimpar: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <Icone nome="search" tamanho={26} className="text-inativo" />
      <p className="text-sm font-semibold text-tinta">{titulo}</p>
      <p className="max-w-[420px] text-[13px] leading-[1.55] text-texto-suave">{descricao}</p>
      <button
        type="button"
        onClick={aoLimpar}
        className="mt-1.5 h-[34px] rounded-sistema border border-borda bg-superficie px-3.5 text-[13px] font-semibold text-texto hover:border-inativo"
      >
        Limpar o recorte
      </button>
    </div>
  )
}
